import type { ReactNode } from "react";

export type FlowTimelineFrame = {
  /** Stable identity of one history visit. Distinct visits must use distinct keys. */
  stateKey: string;
  /**
   * Controls animation between history visits. Consecutive visits with the same
   * key share one animation entry; when omitted, `stateKey` creates its own entry.
   */
  transitionKey?: string;
  content: ReactNode;
};

export type FlowTimelineTransitionGroup = {
  /** Stable identity of this animation entry. */
  stateKey: string;
  /** Consecutive history visits represented by this animation entry. */
  visits: FlowTimelineFrame[];
};

export function groupFlowTimelineFrames(
  frames: readonly FlowTimelineFrame[],
): FlowTimelineTransitionGroup[] {
  const groups: FlowTimelineTransitionGroup[] = [];

  for (const frame of frames) {
    const transitionKey = frame.transitionKey ?? frame.stateKey;
    const previousGroup = groups.at(-1);
    const previousVisit = previousGroup?.visits.at(-1);
    const previousTransitionKey =
      previousVisit?.transitionKey ?? previousVisit?.stateKey;

    if (previousGroup && previousTransitionKey === transitionKey) {
      previousGroup.visits.push(frame);
    } else {
      groups.push({ stateKey: frame.stateKey, visits: [frame] });
    }
  }

  return groups;
}

export type FlowTimelineTransitionProps = {
  /** Every mounted history visit, including visits ahead of the active cursor. */
  frames: readonly FlowTimelineFrame[];
  /** `stateKey` of the history visit currently selected by the flow cursor. */
  activeStateKey: string;
};
